import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import axios from "axios";
import { formatDualCurrency, FX_CONFIG } from "../config/fx";
import { 
  Package,
  MapPin,
  Clock,
  ArrowLeft,
  CheckCircle2,
  ShoppingCart
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const ProductDetail = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const getImageUrl = (paths) => {
    if (!paths || paths.length === 0) return null;
    const path = paths[0];
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_URL}${path}`;
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${productId}`);
      setProduct(response.data);
    } catch (err) {
      console.error('Failed to fetch product:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="container-main py-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Skeleton className="h-96 rounded-md" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-main py-16 text-center">
        <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Product not found</h2>
        <Link to="/marketplace">
          <Button variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Marketplace
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="product-detail-page">
      <div className="container-main py-8">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-slate-500 mb-8">
          <Link to="/marketplace" className="hover:text-[#0D4F8B]">Marketplace</Link>
          <span>/</span>
          <span className="text-slate-900">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            <Card className="card-base overflow-hidden">
              <div className="h-96 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                {getImageUrl(product.image_paths) ? (
                  <img
                    src={getImageUrl(product.image_paths)}
                    alt={product.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Package className="w-24 h-24 text-slate-300" aria-hidden="true" />
                )}
              </div>
            </Card>
          </div>

          {/* Product Info */}
          <div>
            <Badge variant="outline" className="mb-4">
              {product.category}
            </Badge>
            
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {product.name}
            </h1>

            {/* Supplier Info */}
            {product.supplier_name && (
              <div className="flex items-center gap-2 text-slate-600 mb-6">
                <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
                <span>Verified Supplier: </span>
                <Link to={`/suppliers/${product.supplier_id}`} className="text-[#0D4F8B] hover:underline font-medium">
                  {product.supplier_name}
                </Link>
                {product.supplier_location && (
                  <span className="text-slate-400">• {product.supplier_location}</span>
                )}
              </div>
            )}

            {/* Price */}
            <Card className="card-base mb-6">
              <CardContent className="p-6">
                <div className="text-sm text-slate-500 mb-1">{t('product.priceRange')}</div>
                <div className="text-3xl font-bold text-[#0D4F8B] mb-1">
                  {formatDualCurrency(product.price_min_usd).usd} - {formatDualCurrency(product.price_max_usd).usd}
                </div>
                <div className="text-sm text-slate-500">
                  {formatDualCurrency(product.price_min_usd).aed} - {formatDualCurrency(product.price_max_usd).aed}
                </div>
                <p className="text-xs text-slate-400 mt-2">{FX_CONFIG.disclaimer}</p>
              </CardContent>
            </Card>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card className="card-base">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 text-[#0D4F8B] mx-auto mb-2" />
                  <div className="text-sm text-slate-500">{t('product.moq')}</div>
                  <div className="font-bold text-slate-900">{product.moq} units</div>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 text-[#0D4F8B] mx-auto mb-2" />
                  <div className="text-sm text-slate-500">{t('product.leadTime')}</div>
                  <div className="font-bold text-slate-900">{product.lead_time_days} {t('product.days')}</div>
                </CardContent>
              </Card>
            </div>

            {/* Specs */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold text-slate-900 mb-3">{t('product.specs')}</h3>
                <Card className="card-base">
                  <CardContent className="p-4">
                    <dl className="grid grid-cols-2 gap-4 text-sm">
                      {Object.entries(product.specs).map(([key, value]) => (
                        <div key={key}>
                          <dt className="text-slate-500">{key}</dt>
                          <dd className="font-medium text-slate-900">{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* CTA */}
            <div className="flex gap-4">
              <Link to={`/rfq?product=${product.id}&category=${product.category}`} className="flex-1">
                <Button className="w-full btn-cta" data-testid="request-quote-btn">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {t('marketplace.requestQuote')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;


